/**
 * §P.6 fase 2 · El chat con las familias.
 *
 * ## Solo texto, y eso se dice en pantalla
 *
 * No hay adjuntos y no es una limitación pendiente: «un canal por el que un adulto
 * puede mandarle una imagen a la familia de un menor es justo el riesgo que no
 * queremos, y **sin columna no se puede añadir por descuido**». La pantalla lo dice
 * junto al campo, porque un cuadro de texto sin clip invita a preguntar por qué no
 * hay clip.
 *
 * ## Nunca con un menor
 *
 * `conversation_participants` no admite a un alumno menor: se habla con el tutor. Por
 * eso la lista enseña **de quién se habla** —`student_id`— y **con quién se habla**
 * —los tutores— como dos cosas distintas, que es lo que evita el «¿de cuál de tus dos
 * hijos me hablas?».
 *
 * ## Se abre desde el alumno, no desde una lista de personas
 *
 * Los destinatarios se comprueban contra la base —tienen que ser tutores de este club
 * y de ese alumno—, así que la pantalla pide primero el alumno y de ahí saca los
 * tutores. Sin eso, abrir una conversación sería «la forma más fácil de escribirle a
 * cualquier persona con cuenta en Kiap tecleando su `user_id`».
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import {
  etiquetaDeParte,
  tituloDeConversacion,
  tutoresDe,
  type Conversacion,
  type Mensaje,
} from '@/club/avisos/comunicacion';

export function Chat({
  club,
  yoSoy,
  puedeEscribir,
}: {
  club: string;
  /** Mi `user_id`, para saber qué mensajes son míos. */
  yoSoy: string;
  puedeEscribir: boolean;
}) {
  const cache = useQueryClient();
  const [abierta, setAbierta] = useState<Conversacion | null>(null);
  const [texto, setTexto] = useState('');
  const [conCerradas, setConCerradas] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);

  const conversaciones = useQuery({
    queryKey: ['conversaciones', club, conCerradas],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).conversations.get({
        query: conCerradas
          ? { include_closed: true, limit: 100 }
          : { limit: 100 },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Conversacion[];
    },
  });

  const mensajes = useQuery({
    queryKey: ['mensajes', club, abierta?.id],
    enabled: abierta !== null,
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .conversations({ conversationId: abierta!.id })
        .messages.get({ query: { limit: 500 } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Mensaje[];
    },
  });

  const enviar = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .conversations({ conversationId: abierta!.id })
        .messages.post(
          { body: texto.trim() },
          // Un mensaje es un envío: clave nueva por pulsación.
          { headers: { 'idempotency-key': crypto.randomUUID() } }
        );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setTexto('');
      void cache.invalidateQueries({
        queryKey: ['mensajes', club, abierta?.id],
      });
      void cache.invalidateQueries({ queryKey: ['conversaciones', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  // ── Un hilo ──────────────────────────────────────────────────────────────
  if (abierta) {
    const tutores = tutoresDe(abierta);

    return (
      <section>
        <header className="titulo-fila">
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setAbierta(null);
              setProblema(null);
            }}
          >
            ← Conversaciones
          </button>
        </header>

        <h1>{tituloDeConversacion(abierta)}</h1>

        <p className="tenue">
          {/*
            Con quién se habla y de quién se habla, separados: son dos cosas y
            confundirlas es el «¿de cuál de tus dos hijos me hablas?».
          */}
          Con{' '}
          {tutores
            .map((p) => `${p.name} (${etiquetaDeParte(p.party).toLowerCase()})`)
            .join(', ')}
          {abierta.student_name ? ` · sobre ${abierta.student_name}` : ''}
          {abierta.closed_at ? ' · cerrada' : ''}
        </p>

        {mensajes.isPending && (
          <Cargando filas={4} etiqueta="Cargando mensajes" />
        )}

        {mensajes.isError && (
          <PanelError
            problema={aProblema(mensajes.error)}
            reintentar={() => void mensajes.refetch()}
          />
        )}

        {mensajes.data && (
          <ul className="mensajes">
            {mensajes.data.map((m) => (
              <li
                key={m.id}
                className={m.sender_user_id === yoSoy ? 'mio' : undefined}
              >
                <p className="mensaje-cuerpo">
                  {/* Un mensaje borrado deja hueco a propósito: pasó algo. */}
                  {m.deleted_at ? (
                    <span className="tenue">Este mensaje se borró.</span>
                  ) : (
                    m.body
                  )}
                </p>
                <p className="tenue">
                  {m.sender_name} · {fechaYHora(m.sent_at)}
                  {m.edited_at ? ' · editado' : ''}
                </p>
              </li>
            ))}
          </ul>
        )}

        {problema && <PanelError problema={problema} />}

        {puedeEscribir && !abierta.closed_at && (
          <form
            className="formulario"
            onSubmit={(e) => {
              e.preventDefault();
              setProblema(null);
              enviar.mutate();
            }}
          >
            <label>
              Escribir
              <textarea
                required
                rows={3}
                maxLength={4000}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
              />
            </label>

            {/* Por qué no hay clip. Ver la cabecera. */}
            <p className="tenue">
              <strong>Solo texto, y a propósito.</strong> Este canal no admite
              fotos ni ficheros: es una conversación con la familia de un menor
              y el riesgo no compensa. Para mandar un papel, usa el correo.
            </p>

            <div className="acciones">
              <button
                type="submit"
                disabled={enviar.isPending || texto.trim() === ''}
              >
                {enviar.isPending ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </form>
        )}

        {abierta.closed_at && (
          <p className="tenue">
            Esta conversación está cerrada. Si hay algo nuevo que decir, abre
            otra desde la ficha del alumno.
          </p>
        )}
      </section>
    );
  }

  // ── La bandeja ───────────────────────────────────────────────────────────
  if (conversaciones.isPending) {
    return <Cargando filas={4} etiqueta="Cargando las conversaciones" />;
  }

  if (conversaciones.isError || !conversaciones.data) {
    return (
      <PanelError
        problema={aProblema(conversaciones.error)}
        reintentar={() => void conversaciones.refetch()}
      />
    );
  }

  const lista = conversaciones.data;
  const sinLeer = lista.reduce((s, c) => s + c.unread, 0);

  return (
    <section>
      <header className="titulo-fila">
        <h1>Chat</h1>
        <button
          type="button"
          className="btn-plano"
          onClick={() => setConCerradas((x) => !x)}
        >
          {conCerradas ? 'Solo las abiertas' : 'Ver también las cerradas'}
        </button>
      </header>

      <p className="tenue">
        Conversaciones con las familias.{' '}
        <strong>Solo texto y nunca con un menor</strong>: se habla con el tutor,
        aunque el hilo vaya de su hijo.
      </p>

      {sinLeer > 0 && (
        <div className="aviso aviso-calma" role="status">
          <strong>
            {sinLeer === 1
              ? 'Tienes un mensaje sin leer.'
              : `Tienes ${sinLeer} mensajes sin leer.`}
          </strong>
        </div>
      )}

      {lista.length === 0 ? (
        <Vacio
          titulo="No hay conversaciones"
          explicacion="Una conversación se abre desde la ficha del alumno, para que los destinatarios sean sus tutores y no cualquiera con cuenta en Kiap."
        />
      ) : (
        <ul className="conversaciones">
          {lista.map((c) => (
            <li key={c.id} className={c.closed_at ? 'fila-fuera' : undefined}>
              <div className="aviso-cabeza">
                <button
                  type="button"
                  className="enlace-fila"
                  onClick={() => setAbierta(c)}
                >
                  <strong>{tituloDeConversacion(c)}</strong>
                </button>
                {c.unread > 0 && (
                  <span className="pastilla pastilla-info">
                    {c.unread} sin leer
                  </span>
                )}
                {c.closed_at && <span className="pastilla">Cerrada</span>}
              </div>

              {/* El último mensaje: es lo que dice si hay que entrar o no. */}
              {c.last_message_preview && (
                <p className="tenue">{c.last_message_preview}</p>
              )}

              <div className="aviso-pie">
                <span className="tenue">
                  {tutoresDe(c)
                    .map((p) => p.name)
                    .join(', ')}
                  {c.student_name ? ` · sobre ${c.student_name}` : ''}
                </span>
                <span className="tenue">
                  {c.last_message_at
                    ? fechaYHora(c.last_message_at)
                    : `abierta ${fechaYHora(c.created_at)}`}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
